import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SeoHead, { BASE_URL } from "@/components/feature/SeoHead";
import HeroSection from "./components/HeroSection";
import TrustBanner from "./components/TrustBanner";
import CategoriesSection from "./components/CategoriesSection";
import DropshippingSection from "./components/DropshippingSection";
import InfluencerSection from "./components/InfluencerSection";
import FeaturedProducts from "./components/FeaturedProducts";
import RFQSection from "./components/RFQSection";
import CountryFactoriesSection from "./components/CountryFactoriesSection";
import InfantDonationSection from "./components/InfantDonationSection";
import { dubaiFactories } from "@/mocks/dubaiFactories";
import { saudiFactories } from "@/mocks/saudiFactories";
import { qatarFactories } from "@/mocks/qatarFactories";
import { kuwaitFactories } from "@/mocks/kuwaitFactories";
import { omanFactories } from "@/mocks/omanFactories";
import { bahrainFactories } from "@/mocks/bahrainFactories";
import { turkeyFactories } from "@/mocks/turkeyFactories";
import { pakistanFactories } from "@/mocks/pakistanFactories";
import { israelFactories } from "@/mocks/israelFactories";
import { Factory } from "@/components/feature/FactoryCard";

const HomePage = () => (
  <div className="min-h-screen">
    <SeoHead
      title="SuperMiddleEast – Buy Direct from Verified Factories | Dubai, Saudi, Turkey, Pakistan"
      description="SuperMiddleEast connects global buyers with verified factories in Dubai, Saudi Arabia, Qatar, Kuwait, Oman, Bahrain, Turkey and Pakistan. Sample orders, bulk orders and dropshipping from 5 million+ verified factories."
      keywords="verified factories, buy direct factory, dropshipping Middle East, Dubai factory, Saudi Arabia manufacturer, Turkey factory, Pakistan factory, wholesale supplier"
      canonical={BASE_URL}
      jsonLd={[
        {
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "SuperMiddleEast – Buy Direct from Verified Factories",
          "description": "Global B2B marketplace connecting buyers with verified factories in Dubai, Saudi Arabia, Turkey, Pakistan and more.",
          "url": BASE_URL,
          "inLanguage": "en",
        },
        {
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": "Factory Directories by Country",
          "description": "Browse verified factories by country across the Middle East, Turkey and Pakistan.",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Dubai Factories", "url": `${BASE_URL}/dubai` },
            { "@type": "ListItem", "position": 2, "name": "Saudi Arabia Factories", "url": `${BASE_URL}/saudi-arabia` },
            { "@type": "ListItem", "position": 3, "name": "Qatar Factories", "url": `${BASE_URL}/qatar` },
            { "@type": "ListItem", "position": 4, "name": "Kuwait Factories", "url": `${BASE_URL}/kuwait` },
            { "@type": "ListItem", "position": 5, "name": "Oman Factories", "url": `${BASE_URL}/oman` },
            { "@type": "ListItem", "position": 6, "name": "Bahrain Factories", "url": `${BASE_URL}/bahrain` },
            { "@type": "ListItem", "position": 7, "name": "Turkey Factories", "url": `${BASE_URL}/turkey` },
            { "@type": "ListItem", "position": 8, "name": "Pakistan Factories", "url": `${BASE_URL}/pakistan` },
            { "@type": "ListItem", "position": 9, "name": "Israel Factories", "url": `${BASE_URL}/israel` },
          ],
        },
      ]}
    />
    <Navbar />
    <HeroSection />
    <TrustBanner />
    <CategoriesSection />
    <FeaturedProducts />

    {/* Dubai Verified Factories */}
    <CountryFactoriesSection
      country="dubai"
      title="Dubai Verified Factories and Local Warehouse Drop Shipping"
      subtitle="Dubai, United Arab Emirates"
      flag="🇦🇪"
      route="/dubai"
      factories={dubaiFactories as unknown as Factory[]}
      stats={{ products: "5,290+", warehouses: "12+", dropship: 4 }}
      bgClass="bg-gradient-to-b from-amber-50/60 to-white"
    />

    {/* Saudi Arabia Verified Factories */}
    <CountryFactoriesSection
      country="saudi"
      title="Saudi Arabia Verified Factories and Local Warehouse Drop Shipping"
      subtitle="Kingdom of Saudi Arabia"
      flag="🇸🇦"
      route="/saudi-arabia"
      factories={saudiFactories as unknown as Factory[]}
      stats={{ products: "4,100+", warehouses: "8+", dropship: 2 }}
      bgClass="bg-gradient-to-b from-white to-amber-50/40"
    />

    {/* Qatar Verified Factories */}
    <CountryFactoriesSection
      country="qatar"
      title="Qatar Verified Factories and Local Warehouse Drop Shipping"
      subtitle="State of Qatar"
      flag="🇶🇦"
      route="/qatar"
      factories={qatarFactories as unknown as Factory[]}
      stats={{ products: "3,150+", warehouses: "10+", dropship: 4 }}
      bgClass="bg-gradient-to-b from-amber-50/40 to-white"
    />

    {/* Kuwait Verified Factories */}
    <CountryFactoriesSection
      country="kuwait"
      title="Kuwait Verified Factories and Local Warehouse Drop Shipping"
      subtitle="State of Kuwait"
      flag="🇰🇼"
      route="/kuwait"
      factories={kuwaitFactories as unknown as Factory[]}
      stats={{ products: "2,520+", warehouses: "9+", dropship: 3 }}
      bgClass="bg-gradient-to-b from-white to-amber-50/40"
    />

    {/* Oman Verified Factories */}
    <CountryFactoriesSection
      country="oman"
      title="Oman Verified Factories and Local Warehouse Drop Shipping"
      subtitle="Sultanate of Oman"
      flag="🇴🇲"
      route="/oman"
      factories={omanFactories as unknown as Factory[]}
      stats={{ products: "2,630+", warehouses: "10+", dropship: 4 }}
      bgClass="bg-gradient-to-b from-amber-50/40 to-white"
    />

    {/* Bahrain Verified Factories */}
    <CountryFactoriesSection
      country="bahrain"
      title="Bahrain Verified Factories and Local Warehouse Drop Shipping"
      subtitle="Kingdom of Bahrain"
      flag="🇧🇭"
      route="/bahrain"
      factories={bahrainFactories as unknown as Factory[]}
      stats={{ products: "2,830+", warehouses: "9+", dropship: 4 }}
      bgClass="bg-gradient-to-b from-white to-amber-50/40"
    />

    {/* Turkey Verified Factories */}
    <CountryFactoriesSection
      country="turkey"
      title="Turkey Verified Factories and Local Warehouse Drop Shipping"
      subtitle="Republic of Turkey"
      flag="🇹🇷"
      route="/turkey"
      factories={turkeyFactories as unknown as Factory[]}
      stats={{ products: "6,480+", warehouses: "12+", dropship: 5 }}
      bgClass="bg-gradient-to-b from-amber-50/40 to-white"
    />

    {/* Pakistan Verified Factories */}
    <CountryFactoriesSection
      country="pakistan"
      title="Pakistan Verified Factories and Local Warehouse Drop Shipping"
      subtitle="Islamic Republic of Pakistan"
      flag="🇵🇰"
      route="/pakistan"
      factories={pakistanFactories as unknown as Factory[]}
      stats={{ products: "9,510+", warehouses: "14+", dropship: 4 }}
      bgClass="bg-gradient-to-b from-white to-amber-50/40"
    />

    {/* Israel Verified Factories */}
    <CountryFactoriesSection
      country="israel"
      title="Israel Verified Factories and Local Warehouse Drop Shipping"
      subtitle="State of Israel"
      flag="🇮🇱"
      route="/israel"
      factories={israelFactories as unknown as Factory[]}
      stats={{ products: "7,880+", warehouses: "13+", dropship: 5 }}
      bgClass="bg-gradient-to-b from-amber-50/40 to-white"
    />

    {/* Infant Donation */}
    <InfantDonationSection />

    <DropshippingSection />
    <InfluencerSection />
    <RFQSection />
    <Footer />
  </div>
);

export default HomePage;
